import React, { useRef, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  IconButton,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Tooltip,
  Typography,
  alpha,
  useTheme,
} from '@mui/material';
import {
  Close as CloseIcon,
  Upload as UploadIcon,
  FileDownload as DownloadIcon,
  CheckCircle as CheckIcon,
  ErrorOutline as ErrorIcon,
  Warning as WarnIcon,
} from '@mui/icons-material';
import inventoryImportService, {
  extractInventoryImportError,
  downloadExistingItemsAsImport,
  type InventoryImportPreviewResponse,
  type InventoryImportPreviewRow,
  type InventoryImportCommitResponse,
  type InventoryImportCommitRow,
  type ImportAction,
  type CommitStatus,
} from '../../services/inventoryImportService';
import { downloadInventoryImportTemplate } from '../../utils/inventoryImportTemplate';

// ── Types ─────────────────────────────────────────────────────────────────────

type Step = 'idle' | 'previewing' | 'previewed' | 'committing' | 'committed';

interface Props {
  open: boolean;
  venueId: string;
  onClose: () => void;
  onSuccess: () => void;
}

// ── Helpers ───────────────────────────────────────────────────────────────────

function rowLabel(row: InventoryImportPreviewRow): string {
  return (
    row.resolved.inventoryDisplayName ||
    row.data.itemName ||
    `Row ${row.rowNumber}`
  );
}

function commitRowLabel(
  row: InventoryImportCommitRow,
  preview: InventoryImportPreviewRow | undefined,
): string {
  if (preview) return rowLabel(preview);
  return `Row ${row.rowNumber}`;
}

// ── Sub-components ────────────────────────────────────────────────────────────

function ActionChip({ action }: { action: ImportAction }) {
  const cfg = {
    CREATE: { label: 'Create', color: '#1a7f37', bg: '#dafbe1' },
    UPDATE: { label: 'Update', color: '#0969da', bg: '#ddf4ff' },
    ERROR:  { label: 'Error',  color: '#cf222e', bg: '#ffebe9' },
  }[action];
  return (
    <Chip
      label={cfg.label}
      size="small"
      sx={{ fontWeight: 700, fontSize: '0.68rem', color: cfg.color, backgroundColor: cfg.bg, border: 'none', height: 20 }}
    />
  );
}

function StatusChip({ status }: { status: CommitStatus }) {
  const cfg = {
    SUCCESS: { label: 'Success', color: '#1a7f37', bg: '#dafbe1' },
    ERROR:   { label: 'Error',   color: '#cf222e', bg: '#ffebe9' },
    SKIPPED: { label: 'Skipped', color: '#57606a', bg: '#f6f8fa' },
  }[status];
  return (
    <Chip
      label={cfg.label}
      size="small"
      sx={{ fontWeight: 700, fontSize: '0.68rem', color: cfg.color, backgroundColor: cfg.bg, border: 'none', height: 20 }}
    />
  );
}

function SummaryChips({
  items,
}: {
  items: { label: string; value: number; color: string; bg: string }[];
}) {
  return (
    <Box sx={{ display: 'flex', gap: 1.5, flexWrap: 'wrap', alignItems: 'center' }}>
      {items.map(item => (
        <Box key={item.label} sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
          <Chip
            label={item.value}
            size="small"
            sx={{
              fontWeight: 800,
              fontSize: '0.78rem',
              color: item.color,
              backgroundColor: item.bg,
              border: 'none',
              minWidth: 28,
              height: 22,
            }}
          />
          <Typography variant="caption" color="text.secondary" fontWeight={500}>
            {item.label}
          </Typography>
        </Box>
      ))}
    </Box>
  );
}

// ── Preview table ─────────────────────────────────────────────────────────────

function PreviewTable({ rows }: { rows: InventoryImportPreviewRow[] }) {
  const theme = useTheme();
  return (
    <TableContainer sx={{ maxHeight: 360 }}>
      <Table size="small" stickyHeader>
        <TableHead>
          <TableRow>
            {['Row', 'Action', 'Item', 'Supplier', 'Wine List', 'Issues'].map(h => (
              <TableCell
                key={h}
                sx={{
                  fontWeight: 600,
                  fontSize: '0.68rem',
                  textTransform: 'uppercase',
                  letterSpacing: '0.05em',
                  color: alpha(theme.palette.text.primary, 0.45),
                  backgroundColor: theme.palette.background.paper,
                  py: 1,
                  whiteSpace: 'nowrap',
                }}
              >
                {h}
              </TableCell>
            ))}
          </TableRow>
        </TableHead>
        <TableBody>
          {rows.map(row => (
            <TableRow
              key={row.rowNumber}
              sx={{
                '& td': { borderColor: theme.palette.divider },
                backgroundColor:
                  row.action === 'ERROR' ? alpha('#cf222e', 0.03) : undefined,
              }}
            >
              <TableCell sx={{ py: 0.75, color: 'text.secondary', fontSize: '0.75rem', width: 42 }}>
                {row.rowNumber}
              </TableCell>
              <TableCell sx={{ py: 0.75, width: 70 }}>
                <ActionChip action={row.action} />
              </TableCell>
              <TableCell sx={{ py: 0.75, maxWidth: 220 }}>
                <Typography
                  fontSize="0.78rem"
                  noWrap
                  fontWeight={row.action !== 'ERROR' ? 500 : 400}
                >
                  {rowLabel(row)}
                </Typography>
                {row.data.itemType && (
                  <Typography fontSize="0.67rem" color="text.disabled">
                    {row.data.itemType === 'WINE' ? 'Wine' : row.data.itemType === 'MANUFACTURED' ? 'Manufactured' : 'Purchased'}
                    {row.data.sizeValue
                      ? ` · ${row.data.sizeValue}${row.data.sizeUnit ?? ''}`
                      : ''}
                  </Typography>
                )}
              </TableCell>
              <TableCell sx={{ py: 0.75, maxWidth: 140 }}>
                {row.data.supplierName ? (
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                    <Typography fontSize="0.75rem" color="text.secondary" noWrap>
                      {row.data.supplierName}
                    </Typography>
                    {row.resolved.supplierAction === 'AUTO_CREATE' && (
                      <Tooltip title="Will be created automatically on confirm">
                        <Chip
                          label="New"
                          size="small"
                          sx={{
                            fontSize: '0.6rem',
                            height: 16,
                            fontWeight: 600,
                            color: '#9a6700',
                            backgroundColor: '#fef9c3',
                            border: 'none',
                          }}
                        />
                      </Tooltip>
                    )}
                  </Box>
                ) : (
                  <Typography fontSize="0.75rem" color="text.disabled">—</Typography>
                )}
              </TableCell>
              <TableCell sx={{ py: 0.75, maxWidth: 120 }}>
                {row.data.wineListName ? (
                  <Typography fontSize="0.75rem" color="text.secondary" noWrap>
                    {row.data.wineListName}
                  </Typography>
                ) : (
                  <Typography fontSize="0.75rem" color="text.disabled">—</Typography>
                )}
              </TableCell>
              <TableCell sx={{ py: 0.75, maxWidth: 260 }}>
                {row.errors.length > 0 && (
                  <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.25 }}>
                    {row.errors.map((e, idx) => (
                      <Box key={idx} sx={{ display: 'flex', alignItems: 'flex-start', gap: 0.5 }}>
                        <ErrorIcon sx={{ fontSize: 12, color: '#cf222e', mt: 0.25, flexShrink: 0 }} />
                        <Typography fontSize="0.72rem" color="#cf222e" sx={{ lineHeight: 1.4 }}>
                          {e}
                        </Typography>
                      </Box>
                    ))}
                  </Box>
                )}
                {row.errors.length === 0 && row.warnings.length > 0 && (
                  <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 0.5 }}>
                    <WarnIcon sx={{ fontSize: 12, color: '#9a6700', mt: 0.25, flexShrink: 0 }} />
                    <Typography fontSize="0.72rem" color="#9a6700" sx={{ lineHeight: 1.4 }}>
                      {row.warnings[0]}
                    </Typography>
                  </Box>
                )}
                {row.errors.length === 0 && row.warnings.length === 0 && (
                  <CheckIcon sx={{ fontSize: 14, color: '#1a7f37' }} />
                )}
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </TableContainer>
  );
}

// ── Commit result table ───────────────────────────────────────────────────────

function CommitTable({
  rows,
  previewRows,
}: {
  rows: InventoryImportCommitRow[];
  previewRows: InventoryImportPreviewRow[];
}) {
  const theme = useTheme();
  const previewMap = new Map(previewRows.map(r => [r.rowNumber, r]));

  return (
    <TableContainer sx={{ maxHeight: 360 }}>
      <Table size="small" stickyHeader>
        <TableHead>
          <TableRow>
            {['Row', 'Status', 'Action', 'Item', 'Details', 'Errors'].map(h => (
              <TableCell
                key={h}
                sx={{
                  fontWeight: 600,
                  fontSize: '0.68rem',
                  textTransform: 'uppercase',
                  letterSpacing: '0.05em',
                  color: alpha(theme.palette.text.primary, 0.45),
                  backgroundColor: theme.palette.background.paper,
                  py: 1,
                  whiteSpace: 'nowrap',
                }}
              >
                {h}
              </TableCell>
            ))}
          </TableRow>
        </TableHead>
        <TableBody>
          {rows.map(row => {
            const preview = previewMap.get(row.rowNumber);
            return (
              <TableRow
                key={row.rowNumber}
                sx={{
                  '& td': { borderColor: theme.palette.divider },
                  backgroundColor:
                    row.status === 'ERROR'
                      ? alpha('#cf222e', 0.03)
                      : row.status === 'SKIPPED'
                        ? alpha('#57606a', 0.03)
                        : undefined,
                }}
              >
                <TableCell sx={{ py: 0.75, color: 'text.secondary', fontSize: '0.75rem', width: 42 }}>
                  {row.rowNumber}
                </TableCell>
                <TableCell sx={{ py: 0.75, width: 76 }}>
                  <StatusChip status={row.status} />
                </TableCell>
                <TableCell sx={{ py: 0.75, width: 70 }}>
                  {row.action !== 'ERROR' ? (
                    <ActionChip action={row.action} />
                  ) : (
                    <Typography fontSize="0.72rem" color="text.disabled">—</Typography>
                  )}
                </TableCell>
                <TableCell sx={{ py: 0.75, maxWidth: 220 }}>
                  <Typography fontSize="0.78rem" noWrap>
                    {commitRowLabel(row, preview)}
                  </Typography>
                </TableCell>
                <TableCell sx={{ py: 0.75, maxWidth: 160 }}>
                  {row.status === 'SUCCESS' && (
                    <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap' }}>
                      {row.result.supplierCreated && (
                        <Chip
                          label="Supplier created"
                          size="small"
                          sx={{ fontSize: '0.6rem', height: 16, fontWeight: 600, color: '#0969da', backgroundColor: '#ddf4ff', border: 'none' }}
                        />
                      )}
                      {row.result.supplierLinked && !row.result.supplierCreated && (
                        <Chip
                          label="Supplier linked"
                          size="small"
                          sx={{ fontSize: '0.6rem', height: 16, color: '#57606a', backgroundColor: '#f6f8fa', border: 'none' }}
                        />
                      )}
                      {row.result.wineListLinked && (
                        <Chip
                          label="Wine list linked"
                          size="small"
                          sx={{ fontSize: '0.6rem', height: 16, color: '#6941c6', backgroundColor: alpha('#6941c6', 0.08), border: 'none' }}
                        />
                      )}
                      {!row.result.supplierLinked && !row.result.wineListLinked && (
                        <Typography fontSize="0.72rem" color="text.disabled">Item only</Typography>
                      )}
                    </Box>
                  )}
                </TableCell>
                <TableCell sx={{ py: 0.75, maxWidth: 240 }}>
                  {row.errors.length > 0 && (
                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.25 }}>
                      {row.errors.map((e, idx) => (
                        <Typography key={idx} fontSize="0.72rem" color="#cf222e" sx={{ lineHeight: 1.4 }}>
                          {e}
                        </Typography>
                      ))}
                    </Box>
                  )}
                </TableCell>
              </TableRow>
            );
          })}
        </TableBody>
      </Table>
    </TableContainer>
  );
}

// ── Main dialog ───────────────────────────────────────────────────────────────

const InventoryImportDialog: React.FC<Props> = ({ open, venueId, onClose, onSuccess }) => {
  const theme = useTheme();
  const fileInputRef = useRef<HTMLInputElement>(null);

  const [step, setStep] = useState<Step>('idle');
  const [file, setFile] = useState<File | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [preview, setPreview] = useState<InventoryImportPreviewResponse | null>(null);
  const [commitResult, setCommitResult] = useState<InventoryImportCommitResponse | null>(null);
  const [exportingItems, setExportingItems] = useState(false);

  const didCommitSuccessfully =
    commitResult !== null &&
    (commitResult.summary.created + commitResult.summary.updated) > 0;

  const handleClose = () => {
    if (step === 'previewing' || step === 'committing') return;
    if (didCommitSuccessfully) onSuccess();
    onClose();
    setTimeout(() => {
      setStep('idle');
      setFile(null);
      setError(null);
      setPreview(null);
      setCommitResult(null);
    }, 300);
  };

  const handleChooseFile = () => {
    // Reset before opening so the user can re-pick the same file and get a fresh onChange.
    if (fileInputRef.current) fileInputRef.current.value = '';
    fileInputRef.current?.click();
  };

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const f = e.target.files?.[0] ?? null;
    setFile(f);
    setError(null);
    setPreview(null);
    setStep('idle');
  };

  const handleDownloadExisting = async () => {
    if (exportingItems) return;
    setExportingItems(true);
    try {
      const rows = await inventoryImportService.exportItems(venueId);
      downloadExistingItemsAsImport(rows);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to export existing items.');
    } finally {
      setExportingItems(false);
    }
  };

  const handlePreview = async () => {
    if (!file) return;
    setStep('previewing');
    setError(null);
    try {
      const result = await inventoryImportService.preview(venueId, file);
      setPreview(result);
      setStep('previewed');
    } catch (err) {
      setError(extractInventoryImportError(err));
      setStep('idle');
    }
  };

  const handleCommit = async () => {
    if (!file) return;
    setStep('committing');
    setError(null);
    try {
      const result = await inventoryImportService.commit(venueId, file);
      setCommitResult(result);
      setStep('committed');
    } catch (err) {
      setError(extractInventoryImportError(err));
      setStep('previewed');
    }
  };

  const canCommit =
    step === 'previewed' &&
    preview !== null &&
    preview.summary.create + preview.summary.update > 0;

  const isBusy = step === 'previewing' || step === 'committing';

  return (
    <Dialog
      open={open}
      onClose={isBusy ? undefined : handleClose}
      maxWidth="lg"
      fullWidth
      PaperProps={{ sx: { borderRadius: 3, minHeight: 340 } }}
    >
      <DialogTitle sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', pb: 1.5 }}>
        <Typography variant="h6" fontWeight={700}>
          {step === 'committed' ? 'Import Complete' : 'Import Inventory Items'}
        </Typography>
        <IconButton size="small" onClick={handleClose} disabled={isBusy}>
          <CloseIcon fontSize="small" />
        </IconButton>
      </DialogTitle>

      <Divider />

      <DialogContent sx={{ pt: 2.5 }}>
        {/* Single hidden file input — always mounted so ref stays valid */}
        <input
          ref={fileInputRef}
          type="file"
          accept=".xlsx,.csv"
          onChange={handleFileChange}
          style={{ display: 'none' }}
        />

        {/* ── Error banner ────────────────────────────────────────────── */}
        {error && (
          <Alert severity="error" onClose={() => setError(null)} sx={{ mb: 2, borderRadius: 2 }}>
            {error}
          </Alert>
        )}

        {/* ── IDLE — file selection ─────────────────────────────────── */}
        {step === 'idle' && (
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2.5 }}>
            <Typography variant="body2" color="text.secondary">
              Upload an <strong>.xlsx</strong> file to preview and import inventory items.
              Use the template to ensure columns are correct, or export your current items as a starting point.
            </Typography>

            {/* Download actions */}
            <Box>
              <Typography variant="caption" color="text.secondary" fontWeight={600} sx={{ display: 'block', mb: 1, textTransform: 'uppercase', letterSpacing: '0.06em' }}>
                Downloads
              </Typography>
              <Box sx={{ display: 'flex', gap: 1.5, flexWrap: 'wrap' }}>
                <Button
                  variant="outlined"
                  size="small"
                  startIcon={<DownloadIcon />}
                  onClick={downloadInventoryImportTemplate}
                  sx={{ textTransform: 'none', borderRadius: 2 }}
                >
                  Download Template
                </Button>
                <Button
                  variant="outlined"
                  size="small"
                  startIcon={exportingItems ? <CircularProgress size={14} /> : <DownloadIcon />}
                  onClick={handleDownloadExisting}
                  disabled={exportingItems}
                  sx={{ textTransform: 'none', borderRadius: 2 }}
                >
                  {exportingItems ? 'Exporting…' : 'Download Existing Items'}
                </Button>
              </Box>
            </Box>

            <Divider />

            {/* File upload */}
            <Box>
              <Typography variant="caption" color="text.secondary" fontWeight={600} sx={{ display: 'block', mb: 1, textTransform: 'uppercase', letterSpacing: '0.06em' }}>
                Upload
              </Typography>
              <Box sx={{ display: 'flex', gap: 1.5, flexWrap: 'wrap', alignItems: 'center' }}>
                <Button
                  variant="outlined"
                  size="small"
                  startIcon={<UploadIcon />}
                  onClick={handleChooseFile}
                  sx={{ textTransform: 'none', borderRadius: 2 }}
                >
                  {file ? 'Change File' : 'Choose File (.xlsx / .csv)'}
                </Button>
                {file && (
                  <Typography variant="body2" color="text.secondary" sx={{ fontStyle: 'italic' }}>
                    {file.name}
                  </Typography>
                )}
              </Box>
            </Box>
          </Box>
        )}

        {/* ── PREVIEWING spinner ─────────────────────────────────────── */}
        {step === 'previewing' && (
          <Box sx={{ py: 6, display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 2 }}>
            <CircularProgress size={32} />
            <Typography variant="body2" color="text.secondary">Analysing file…</Typography>
          </Box>
        )}

        {/* ── PREVIEW results ────────────────────────────────────────── */}
        {step === 'previewed' && preview && (
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
            {/* File line + change */}
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
              <Typography variant="body2" color="text.secondary" sx={{ fontStyle: 'italic' }}>
                {file?.name}
              </Typography>
              <Button
                variant="text"
                size="small"
                onClick={handleChooseFile}
                sx={{ textTransform: 'none', fontSize: '0.75rem' }}
              >
                Change file
              </Button>
            </Box>

            {/* Summary box */}
            <Box
              sx={{
                p: 2,
                borderRadius: 2,
                backgroundColor: alpha(theme.palette.primary.main, 0.04),
                border: `1px solid ${alpha(theme.palette.primary.main, 0.12)}`,
              }}
            >
              <Typography
                variant="caption"
                color="text.secondary"
                fontWeight={600}
                sx={{ display: 'block', mb: 1, textTransform: 'uppercase', letterSpacing: '0.06em' }}
              >
                Preview — {preview.totalRows} {preview.totalRows === 1 ? 'row' : 'rows'}
              </Typography>
              <SummaryChips items={[
                { label: 'will be created', value: preview.summary.create, color: '#1a7f37', bg: '#dafbe1' },
                { label: 'will be updated', value: preview.summary.update, color: '#0969da', bg: '#ddf4ff' },
                { label: 'have errors',     value: preview.summary.error,  color: '#cf222e', bg: '#ffebe9' },
              ]} />
              {preview.summary.error > 0 && preview.summary.create + preview.summary.update === 0 && (
                <Alert severity="error" sx={{ mt: 1.5, borderRadius: 2, py: 0.5 }}>
                  All rows have errors — fix the file and preview again.
                </Alert>
              )}
              {preview.summary.error > 0 && preview.summary.create + preview.summary.update > 0 && (
                <Alert severity="warning" sx={{ mt: 1.5, borderRadius: 2, py: 0.5 }}>
                  {preview.summary.error} {preview.summary.error === 1 ? 'row has' : 'rows have'} errors and will be skipped on import.
                </Alert>
              )}
            </Box>

            {/* Row table */}
            <Box sx={{ border: `1px solid ${theme.palette.divider}`, borderRadius: 2, overflow: 'hidden' }}>
              <PreviewTable rows={preview.rows} />
            </Box>
          </Box>
        )}

        {/* ── COMMITTING spinner ─────────────────────────────────────── */}
        {step === 'committing' && (
          <Box sx={{ py: 6, display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 2 }}>
            <CircularProgress size={32} />
            <Typography variant="body2" color="text.secondary">Importing items…</Typography>
          </Box>
        )}

        {/* ── COMMITTED result ───────────────────────────────────────── */}
        {step === 'committed' && commitResult && preview && (
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
            <Box
              sx={{
                p: 2,
                borderRadius: 2,
                backgroundColor: alpha('#1a7f37', 0.04),
                border: `1px solid ${alpha('#1a7f37', 0.18)}`,
              }}
            >
              <Typography
                variant="caption"
                color="text.secondary"
                fontWeight={600}
                sx={{ display: 'block', mb: 1, textTransform: 'uppercase', letterSpacing: '0.06em' }}
              >
                Import result — {commitResult.totalRows} {commitResult.totalRows === 1 ? 'row' : 'rows'} processed
              </Typography>
              <SummaryChips items={[
                { label: 'created',          value: commitResult.summary.created, color: '#1a7f37', bg: '#dafbe1' },
                { label: 'updated',          value: commitResult.summary.updated, color: '#0969da', bg: '#ddf4ff' },
                { label: 'errored / skipped', value: commitResult.summary.errored, color: '#57606a', bg: '#f6f8fa' },
              ]} />
            </Box>

            <Box sx={{ border: `1px solid ${theme.palette.divider}`, borderRadius: 2, overflow: 'hidden' }}>
              <CommitTable rows={commitResult.rows} previewRows={preview.rows} />
            </Box>
          </Box>
        )}
      </DialogContent>

      <Divider />

      <DialogActions sx={{ px: 3, py: 2, gap: 1 }}>
        {step === 'idle' && (
          <>
            <Button onClick={handleClose} sx={{ textTransform: 'none' }}>Cancel</Button>
            <Button
              variant="contained"
              disabled={!file}
              onClick={handlePreview}
              sx={{ textTransform: 'none', fontWeight: 600, borderRadius: 2 }}
            >
              Preview Import
            </Button>
          </>
        )}

        {step === 'previewed' && (
          <>
            <Button onClick={handleClose} sx={{ textTransform: 'none' }}>Cancel</Button>
            <Button
              variant="contained"
              disabled={!canCommit}
              onClick={handleCommit}
              sx={{ textTransform: 'none', fontWeight: 600, borderRadius: 2 }}
            >
              Confirm Import
              {canCommit && preview && (
                <Typography component="span" fontSize="0.72rem" sx={{ ml: 0.75, opacity: 0.8 }}>
                  ({preview.summary.create + preview.summary.update} rows)
                </Typography>
              )}
            </Button>
          </>
        )}

        {step === 'committed' && (
          <Button
            variant="contained"
            onClick={handleClose}
            sx={{ textTransform: 'none', fontWeight: 600, borderRadius: 2 }}
          >
            Done
          </Button>
        )}
      </DialogActions>
    </Dialog>
  );
};

export default InventoryImportDialog;
